import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { APP_ROUTES, AUTH_KEYS, removeInboundProductTransitionMarker } from '@core/constants';
import { AuthService } from '..';

@Injectable()
export class AuthorizeGuard {
  constructor(private auth: AuthService, private router: Router) {}

  canActivate(
    _route?: ActivatedRouteSnapshot,
    state?: RouterStateSnapshot
  ): boolean | Promise<boolean> {
    return this.returnAuthorizedOrRedirect(state?.url);
  }

  canActivateChild(
    _route?: ActivatedRouteSnapshot,
    state?: RouterStateSnapshot
  ): boolean | Promise<boolean> {
    return this.returnAuthorizedOrRedirect(state?.url);
  }

  private async returnAuthorizedOrRedirect(targetUrl?: string): Promise<boolean> {
    const safeTargetUrl = targetUrl ? removeInboundProductTransitionMarker(targetUrl) : targetUrl;
    const hadExpiredSession = this.auth.hasExpiredSession;

    if (!this.auth.isAuthenticated) {
      await this.auth.loadExistingAuthSession();
    }

    if (!this.auth.isAuthenticated) {
      if (hadExpiredSession) {
        await this.auth.redirectToSignInForExpiredSession(safeTargetUrl);
        return false;
      }

      const queryParams = this.getReturnQueryParams(safeTargetUrl);
      this.router.navigate([APP_ROUTES.BASE], { queryParams });
      return false;
    }

    if (this.auth.hasPendingRequiredActions && !this.isRequiredActionsRoute(safeTargetUrl)) {
      this.router.navigate([APP_ROUTES.REQUIRED_ACTIONS], {
        queryParams: { [AUTH_KEYS.RETURN_URL]: this.getSafeReturnUrl(safeTargetUrl) }
      });
      return false;
    }

    return true;
  }

  private getReturnQueryParams(targetUrl?: string): Record<string, string> {
    if (!targetUrl || !targetUrl.startsWith('/') || targetUrl.startsWith('//')) {
      return {};
    }

    return { [AUTH_KEYS.RETURN_URL]: targetUrl };
  }

  private getSafeReturnUrl(targetUrl?: string): string {
    return this.getReturnQueryParams(targetUrl)[AUTH_KEYS.RETURN_URL] ?? `/${APP_ROUTES.DASHBOARD}`;
  }

  private isRequiredActionsRoute(targetUrl?: string): boolean {
    const path = targetUrl?.split('?')[0].replace(/^\/+/, '');

    return path === APP_ROUTES.REQUIRED_ACTIONS;
  }
}
